export type AttemptSignal = {
  signal: AbortSignal;
  /**
   * Force-abort this attempt with a TimeoutError reason. Used by stall
   * watchdogs and other internal timeouts to mark the failure as retryable.
   */
  abortWithTimeout: (message: string) => void;
  /**
   * True if our timeout or abortWithTimeout caused the abort, false if the
   * abort came from the caller's external signal (user cancellation).
   */
  isOurAbort: () => boolean;
  cleanup: () => void;
};

export function makeAttemptSignal(
  externalSignal: AbortSignal | undefined,
  overallTimeoutMs: number | undefined,
  label = 'request'
): AttemptSignal {
  const ac = new AbortController();
  let ourAbort = false;

  let overallTid: ReturnType<typeof setTimeout> | undefined;
  if (overallTimeoutMs !== undefined) {
    overallTid = setTimeout(() => {
      ourAbort = true;
      console.warn(`[${label}] overall timeout after ${overallTimeoutMs}ms`);
      ac.abort(
        new DOMException(`${label} timed out after ${overallTimeoutMs}ms`, 'TimeoutError')
      );
    }, overallTimeoutMs);
  }

  let extListener: (() => void) | undefined;
  if (externalSignal) {
    if (externalSignal.aborted) {
      if (overallTid !== undefined) clearTimeout(overallTid);
      ac.abort(externalSignal.reason);
    } else {
      extListener = () => {
        if (overallTid !== undefined) clearTimeout(overallTid);
        ac.abort(externalSignal.reason);
      };
      externalSignal.addEventListener('abort', extListener, { once: true });
    }
  }

  return {
    signal: ac.signal,
    abortWithTimeout: (message) => {
      if (ac.signal.aborted) return;
      ourAbort = true;
      console.warn(`[${label}] ${message}`);
      ac.abort(new DOMException(message, 'TimeoutError'));
    },
    isOurAbort: () => ourAbort,
    cleanup: () => {
      if (overallTid !== undefined) {
        clearTimeout(overallTid);
        overallTid = undefined;
      }
      if (externalSignal && extListener) {
        externalSignal.removeEventListener('abort', extListener);
        extListener = undefined;
      }
    },
  };
}

export type StallWatchdog = { reset: () => void; stop: () => void };

export function makeStallWatchdog(
  stallMs: number,
  onStall: () => void
): StallWatchdog {
  let tid: ReturnType<typeof setTimeout> | undefined;
  return {
    reset: () => {
      if (tid !== undefined) clearTimeout(tid);
      tid = setTimeout(onStall, stallMs);
    },
    stop: () => {
      if (tid !== undefined) {
        clearTimeout(tid);
        tid = undefined;
      }
    },
  };
}
